import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { Form } from 'react-bootstrap';
import { CalendarDays, ListChecks } from 'lucide-react';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import InvoiceItemsTable from './InvoiceItemsTable';
import { getValidationState } from '../utils/invoiceValidation';

/**
 * The invoice itself: two cards, everything visible.
 *
 *   Fakturadetaljer — dates, valuta and moms as labeled fields in one grid,
 *   then the switches (avrundning, and the parent's periodisera).
 *   Rader — the line table, with the invoice note beside the totals.
 *
 * `vatField` / `vatNotes` come from the parent, which owns the treatment
 * logic; this component only places them.
 */

const CURRENCY_OPTIONS = [
    { value: 'SEK', label: 'SEK' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'NOK', label: 'NOK' },
    { value: 'DKK', label: 'DKK' },
];

const toInputDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const InvoiceFormBootstrap = ({
    formData,
    onFormDataChange,
    availableArticles,
    invoiceSettings,
    validationErrors = {},
    vatField = null,
    vatNotes = null,
    extraSwitches = null,
}) => {
    const { companyId } = useParams();
    const [touchedFields, setTouchedFields] = useState({});
    const [fiscalYearWarning, setFiscalYearWarning] = useState(null);

    // Validate invoice date against fiscal years (debounced server check).
    useEffect(() => {
        if (!formData.invoiceDate || !companyId) {
            setFiscalYearWarning(null);
            return undefined;
        }
        const date = formData.invoiceDate instanceof Date ? formData.invoiceDate : new Date(formData.invoiceDate);
        if (Number.isNaN(date.getTime())) {
            setFiscalYearWarning(null);
            return undefined;
        }
        let cancelled = false;
        const timeout = setTimeout(async () => {
            try {
                const url = getApiUrl(`${companyId}/accounting/fiscal-years/validate-date?date=${toInputDate(date)}`);
                const response = await axios.get(url, { withCredentials: true });
                if (!cancelled) setFiscalYearWarning(response.data.warning || null);
            } catch (err) {
                console.error('Error validating fiscal year date:', err);
                if (!cancelled) setFiscalYearWarning(null);
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(timeout); };
    }, [formData.invoiceDate, companyId]);

    const touch = useCallback((field) => setTouchedFields((prev) => ({ ...prev, [field]: true })), []);

    const handleItemsChange = useCallback((items) => {
        touch('items');
        onFormDataChange({ items });
    }, [onFormDataChange, touch]);

    const handleDateChange = useCallback((e, field) => {
        touch(field);
        const date = new Date(e.target.value);
        if (!Number.isNaN(date.getTime())) onFormDataChange({ [field]: date });
    }, [onFormDataChange, touch]);

    const handleFieldChange = useCallback((field, value) => {
        touch(field);
        onFormDataChange({ [field]: value });
    }, [onFormDataChange, touch]);

    const validation = useCallback(
        (field) => getValidationState(field, validationErrors, touchedFields),
        [validationErrors, touchedFields],
    );

    const paymentDays = useMemo(() => {
        if (!(formData.invoiceDate instanceof Date) || !(formData.dueDate instanceof Date)) return null;
        const days = differenceInCalendarDays(formData.dueDate, formData.invoiceDate);
        return Number.isNaN(days) ? null : days;
    }, [formData.invoiceDate, formData.dueDate]);

    const itemCount = formData.items?.length || 0;

    const notesBlock = (
        <div className="inv-notes">
            <label className="inv-label" htmlFor="inv-notes">
                Meddelande på fakturan
                <span className="inv-label__hint">valfritt</span>
            </label>
            <textarea
                id="inv-notes"
                className={`inv-control${validation('notes').isInvalid ? ' is-invalid' : ''}`}
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                maxLength={2000}
                placeholder="Betalningsvillkor, leveransinformation eller annat kunden ska se"
            />
            {validation('notes').isInvalid && (
                <div className="inv-invalid">{validationErrors.notes}</div>
            )}
        </div>
    );

    return (
        <>
            {/* ── Fakturadetaljer ── */}
            <div className="inv-card">
                <div className="inv-card__head">
                    <h3 className="inv-card__title">
                        <CalendarDays strokeWidth={2} />
                        Fakturadetaljer
                    </h3>
                </div>

                <div className="inv-grid inv-grid--fields">
                    <div>
                        <label className="inv-label" htmlFor="inv-invoice-date">Fakturadatum</label>
                        <input
                            id="inv-invoice-date"
                            type="date"
                            className={`inv-control${validation('invoiceDate').isInvalid ? ' is-invalid' : ''}`}
                            value={toInputDate(formData.invoiceDate)}
                            onChange={(e) => handleDateChange(e, 'invoiceDate')}
                            max={toInputDate(new Date())}
                            required
                        />
                        {validation('invoiceDate').isInvalid && (
                            <div className="inv-invalid">{validationErrors.invoiceDate}</div>
                        )}
                    </div>
                    <div>
                        <label className="inv-label" htmlFor="inv-due-date">
                            Förfallodatum
                            {paymentDays !== null && (
                                <span className="inv-label__hint">{paymentDays} dagar</span>
                            )}
                        </label>
                        <input
                            id="inv-due-date"
                            type="date"
                            className={`inv-control${validation('dueDate').isInvalid ? ' is-invalid' : ''}`}
                            value={toInputDate(formData.dueDate)}
                            onChange={(e) => handleDateChange(e, 'dueDate')}
                            min={toInputDate(formData.invoiceDate) || undefined}
                            required
                        />
                        {validation('dueDate').isInvalid && (
                            <div className="inv-invalid">{validationErrors.dueDate}</div>
                        )}
                    </div>
                    <div>
                        <label className="inv-label" htmlFor="inv-currency">Valuta</label>
                        <select
                            id="inv-currency"
                            className={`inv-control${validation('currency').isInvalid ? ' is-invalid' : ''}`}
                            value={formData.currency || 'SEK'}
                            onChange={(e) => handleFieldChange('currency', e.target.value)}
                        >
                            {CURRENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        {validation('currency').isInvalid && (
                            <div className="inv-invalid">{validationErrors.currency}</div>
                        )}
                    </div>
                    {vatField}
                </div>

                {vatNotes}

                {fiscalYearWarning && (
                    <div className="inv-note mt-3" role="alert">{fiscalYearWarning}</div>
                )}

                <div className="inv-switches">
                    <div className="inv-toggle-row">
                        <div>
                            <div className="inv-toggle-row__text">Avrunda totalen till hela kronor</div>
                            <div className="inv-help">Öresavrundningen visas som en egen rad på fakturan</div>
                        </div>
                        <Form.Check
                            type="switch"
                            id="inv-rounding-switch"
                            aria-label="Avrunda totalen till hela kronor"
                            checked={Boolean(formData.roundTotal)}
                            onChange={(e) => onFormDataChange({ roundTotal: e.target.checked })}
                        />
                    </div>
                    {extraSwitches}
                </div>
            </div>

            {/* ── Rader ── */}
            <div className="inv-card">
                <div className="inv-card__head">
                    <h3 className="inv-card__title">
                        <ListChecks strokeWidth={2} />
                        Rader
                    </h3>
                    {itemCount > 0 && (
                        <span className="inv-help">{itemCount} {itemCount === 1 ? 'rad' : 'rader'}</span>
                    )}
                </div>

                <InvoiceItemsTable
                    items={formData.items || []}
                    availableArticles={availableArticles}
                    onItemsChange={handleItemsChange}
                    roundTotal={formData.roundTotal || false}
                    defaultVatRate={formData.defaultVatRate || invoiceSettings?.taxSettings?.defaultVatRate || 25}
                    currency={formData.currency || 'SEK'}
                    vatTreatment={formData.vat_treatment || 'taxable'}
                    companyId={companyId}
                    customerId={formData.customer?.customer_id || formData.customer?.id}
                    footerStart={notesBlock}
                />
                {touchedFields.items && validationErrors.items && (
                    <div className="inv-invalid mt-2">{validationErrors.items}</div>
                )}
            </div>
        </>
    );
};

export default InvoiceFormBootstrap;
